import { useAtom, useAtomValue } from "jotai";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingSpinner } from "chameleon/lib/components";

import {
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import { Modal } from "checkout-experience/components/modal/modal";
import { BrowserSettings } from "checkout-query-client/api/types";

import { PayResponse_DebugInfo } from "frontend-client/export";

import { modalIframe } from "./modal-3ds.css";

interface Modal3DSProps {
  src: string | undefined;
  browserSettings: BrowserSettings;
  setBrowserSettings: Dispatch<SetStateAction<BrowserSettings>>;
}

export const Modal3DS = ({
  src,
  browserSettings,
  setBrowserSettings,
}: Modal3DSProps) => {
  const { t } = useTranslation();
  const [hasIframeContentLoaded, setHasIframeContentLoaded] = useState(false);
  const paymentRequirements = useAtomValue(paymentRequirementsAtom);
  const [paymentMessages, setPaymentMessages] = useAtom(paymentMessagesAtom);

  useEffect(() => {
    // Open modal
    if (!browserSettings?.isModalOpen && paymentRequirements?.threeDs) {
      setBrowserSettings((prevSettings) => ({
        ...prevSettings,
        isModalOpen: true,
      }));
    }
  }, [paymentRequirements]);

  const handleCloseModal = () => {
    setBrowserSettings((prevSettings) => ({
      ...prevSettings,
      isModalOpen: false,
    }));
  };

  const handleIframeLoad = () => {
    setHasIframeContentLoaded(true);
  };

  useEffect(() => {
    // useEffect for 3ds completion listener
    const postMessageListener = (event: {
      type: string;
      data: {
        event: string;
        payload: { status: string; debugInfo: PayResponse_DebugInfo };
      };
    }) => {
      if (event?.data?.event === "3ds-completion") {
        setPaymentMessages({
          threeDsCompletionMessage: event.data.payload,
        });
      }
      return;
    };
    window.addEventListener("message", postMessageListener);

    // cleanup on unmount
    return () => {
      window.removeEventListener("message", postMessageListener);
    };
  }, []);

  useEffect(() => {
    if (
      paymentMessages?.threeDsCompletionMessage?.status === "STATUS_SUCCESS" ||
      paymentMessages?.threeDsCompletionMessage?.status === "STATUS_FAILURE"
    ) {
      handleCloseModal();
    }
  }, [paymentMessages]);

  return (
    <Modal
      data-testid="modal-3ds"
      wrapperId="modal-3ds-wrapper"
      closeModalHandler={handleCloseModal}
      isModalOpen={browserSettings?.isModalOpen}
      title={t("paymentAuthorization")}
      width="60vw" // TODO: get this from the api call
      height="50vw" // TODO: get this from the api call
    >
      <>
        {hasIframeContentLoaded ? null : <LoadingSpinner />}
        <iframe
          data-testid="iframe-3ds"
          className={modalIframe}
          style={{
            display: hasIframeContentLoaded ? "block" : "none",
          }}
          title={t("paymentRequirement.require3ds") ?? ""}
          src={src}
          onLoad={handleIframeLoad}
        />
      </>
    </Modal>
  );
};
